import type { InstallableApp } from "~/pwa/installableApps";

interface RelatedApplication {
	id?: string;
	platform?: string;
	url?: string;
}

type RelatedAppsNavigator = Navigator & {
	getInstalledRelatedApps?: () => Promise<RelatedApplication[]>;
};

const getNavigator = (): RelatedAppsNavigator | null =>
	typeof navigator === "undefined" ? null : (navigator as RelatedAppsNavigator);

export const isAndroid = (): boolean => {
	const nav = getNavigator();
	return nav != null && /android/i.test(nav.userAgent ?? "");
};

export const supportsInstalledAppDetection = (): boolean => {
	const nav = getNavigator();
	return nav != null && typeof nav.getInstalledRelatedApps === "function" && isAndroid();
};

export const WORKSUITE_ROOT_MANIFEST_ID = "/";

const relatedAppCandidates = (related: RelatedApplication): string[] =>
	[related.id, related.url].filter((value): value is string => typeof value === "string");

const candidatePath = (candidate: string): string | null => {
	try {
		return new URL(candidate, "http://app.local").pathname;
	} catch {
		return null;
	}
};

const relatedAppIsWorkSuiteRoot = (related: RelatedApplication): boolean =>
	relatedAppCandidates(related).some((candidate) => candidatePath(candidate) === WORKSUITE_ROOT_MANIFEST_ID);

export const matchesWorkSuiteRoot = (related: RelatedApplication[]): boolean => related.some(relatedAppIsWorkSuiteRoot);

// Android has no JS API to force-launch an installed PWA; navigating to start_url is the best-effort handoff for both installed and browser-open.
const openApp = (app: InstallableApp, _options: { installed?: boolean } = {}): void => {
	if (typeof window === "undefined") return;
	window.location.assign(app.startUrl);
};

export type { RelatedApplication };

export default openApp;
